import {
  APIStatus,
  CheckStatus,
  LanguageConfig,
  SESSION_KEYS,
} from '../config/CustomEnums';
import {
  createOrUpdateMall,
  deleteMalls,
  getMalls,
  getOneMall,
  getPagedMalls,
} from '../services/MallAPIHelper';
import {
  convertCursorToNumber,
  convertNumberToCursor,
  convertPKToId,
  createAction,
  getDisplayLastModifiedBy,
  getFileNameFromUrl,
  getObjectFromSessionStorage,
  removeFromSessionStorage,
  removeUndefinedFieldFromDict,
  saveToSessionStorage,
} from '../utils';
import { createModel } from './BaseModel';
import { apiWithResponseHandle, loading } from './LoadingUtil';

import {
  getDetailPageDisplayLastModifiedDate,
  getListDisplayLastModifiedDate,
} from 'utils/TimeFormatUtil';

const mallSessionKey = SESSION_KEYS.MALL_SESSION_KEY;

export const saveDataToSessionStorage = (data) => {
  saveToSessionStorage(mallSessionKey, data);
};

export const removeDataFromSessionStorage = () => {
  removeFromSessionStorage(mallSessionKey);
};

export const DEFAULT_PHONE_NUMBER_COUNTRY_CODE = {
  label: '852',
  value: '852',
};

const formatInformations = (
  informationsData,
  language,
  defaultLength = null,
) => {
  let informations = {};
  if (informationsData?.edges?.length) {
    for (let i = 0; i < informationsData?.edges?.length; i++) {
      let item = {};
      if (language === LanguageConfig.english) {
        item = informationsData?.edges[i]?.node;
      } else {
        item = informationsData?.edges[i]?.node?.translations?.edges?.filter(
          (item) => item.node.language === language,
        );
        item = item && item.length ? item[0].node : {};
      }
      informations[i] = {
        pk: item.pk || null,
        title: item.title || null,
        content: item.content || null,
        phoneNumberCountryCode: item.phoneNumberCountryCode
          ? {
              label: item.phoneNumberCountryCode,
              value: item.phoneNumberCountryCode,
            }
          : DEFAULT_PHONE_NUMBER_COUNTRY_CODE,
        phoneNumberSubscriberNumber: item.phoneNumberSubscriberNumber || null,
        emailAddress: item.emailAddress || null,
      };
    }
  } else {
    if (defaultLength) {
      for (let i = 0; i < defaultLength; i++) {
        informations[i] = {
          pk: null,
          title: null,
          content: null,
          phoneNumberCountryCode: DEFAULT_PHONE_NUMBER_COUNTRY_CODE,
          phoneNumberSubscriberNumber: null,
          emailAddress: null,
        };
      }
    }
  }
  return informations;
};

const cleanSpecialInformations = (originalData, fieldName) => {
  let data = {};
  if (fieldName === 'phoneNumberInfos') {
    data = {
      title: originalData.title,
      phoneNumberCountryCode: originalData.phoneNumberCountryCode?.value,
      phoneNumberSubscriberNumber: originalData.phoneNumberSubscriberNumber,
    };
  } else if (fieldName === 'emailAddressInfos') {
    data = {
      title: originalData.title,
      emailAddress: originalData.emailAddress,
    };
  } else if (fieldName === 'additionalInfos') {
    data = {
      title: originalData.title,
      content: originalData.content,
    };
  }
  if (originalData.pk) {
    data.id = originalData.pk;
  }
  return data;
};

const cleanInformations = (informations, translations, fieldName) => {
  const cleanedInformations = Object.keys(informations || {})
    .sort()
    .filter((key) => !informations[key].isDeleted)
    .map((key) => {
      const item = informations[key];
      const originalData = {
        id: item.pk || null,
        title: item.title || null,
        content: item.content || null,
        phoneNumberCountryCode: item.phoneNumberCountryCode?.value || null,
        phoneNumberSubscriberNumber: item.phoneNumberSubscriberNumber || null,
        emailAddress: item.emailAddress || null,
        translations: [],
      };
      return cleanSpecialInformations(originalData, fieldName);
    });
  for (let i = 0; i < cleanedInformations?.length; i++) {
    let informationTranslations = [];
    for (let y = 0; y < translations.length; y++) {
      if (translations[y][fieldName]) {
        const originalData = translations[y][fieldName][i];
        informationTranslations.push({
          language: translations[y]['language'],
          ...cleanSpecialInformations(originalData, fieldName),
        });
      }
    }
    cleanedInformations[i]['translations'] = informationTranslations;
  }
  for (let y = 0; y < translations?.length; y++) {
    delete translations[y][fieldName];
  }
  return cleanedInformations;
};

const getTranslations = (data) => {
  const translationList = data.translations?.edges || [];
  const translation = {};
  translationList.forEach((item) => {
    const language = item.node.language;

    const phoneNumberInfos = formatInformations(
      data.phoneNumberInfos,
      language,
      1,
    );

    const emailAddressInfos = formatInformations(
      data.emailAddressInfos,
      language,
      1,
    );

    const additionalInfos = formatInformations(
      data.additionalInfos,
      language,
      1,
    );

    translation[language] = {
      ...item.node,
      name: item.node.name,
      phoneNumberInfos,
      emailAddressInfos,
      additionalInfos,
    };
  });
  translation[LanguageConfig.english] = {
    name: data.name,
    address: data.address,
    pk: data.pk,
  };
  return translation;
};

const postTranslations = (data) => {
  if (!data) {
    return [];
  }
  const languageList = Object.keys(data).filter(
    (language) => language !== LanguageConfig.english,
  );
  const translations = languageList.map((language) => {
    const phoneNumberInfos = Object.keys(data[language].phoneNumberInfos || {})
      .sort()
      .filter((key) => !data[language].phoneNumberInfos[key].isDeleted)
      .map((key) => {
        const item = data[language].phoneNumberInfos[key];
        let infoData = {
          title: item.title || null,
          phoneNumberCountryCode: item.phoneNumberCountryCode?.value || null,
          phoneNumberSubscriberNumber: item.phoneNumberSubscriberNumber || null,
        };
        if (item.pk) {
          infoData.id = item.pk;
        }
        return infoData;
      });

    const emailAddressInfos = Object.keys(
      data[language].emailAddressInfos || {},
    )
      .sort()
      .filter((key) => !data[language].emailAddressInfos[key].isDeleted)
      .map((key) => {
        const item = data[language].emailAddressInfos[key];
        let infoData = {
          title: item.title || null,
          emailAddress: item.emailAddress || null,
        };
        if (item.pk) {
          infoData.id = item.pk;
        }
        return infoData;
      });

    const additionalInfos = Object.keys(data[language].additionalInfos || {})
      .sort()
      .filter((key) => !data[language].additionalInfos[key].isDeleted)
      .map((key) => {
        const item = data[language].additionalInfos[key];
        let infoData = {
          title: item.title || null,
          content: item.content || null,
        };
        if (item.pk) {
          infoData.id = item.pk;
        }
        return infoData;
      });

    const newData = {
      language,
      name: data[language].name,
      // address: data[language].address,
      // phoneNumberInfos,
      // emailAddressInfos,
      // additionalInfos,
    };
    const id = data[language].pk;
    if (id) {
      newData.id = id;
    }
    return newData;
  });
  return translations;
};

const mergeOldData = (origin, old) => {
  const translationLang = Object.keys(origin?.mallTranslations || {}) || [];
  const mallTranslations = {};
  translationLang.forEach((translation) => {
    mallTranslations[translation] = {
      ...old?.mallTranslations?.[translation],
      ...origin?.mallTranslations?.[translation],
    };
    if (!origin.mallPK) {
      delete mallTranslations[translation].pk;
    }
  });
  return {
    ...origin,
    mallTranslations: mallTranslations,
  };
};

const postData = (origin, old) => {
  const name =
    origin.mallTranslations?.[LanguageConfig.english]?.name || origin.name;
  if (!name) {
    return {
      ...origin,
    };
  }
  const data = mergeOldData(origin, old);
  const enData = data.mallTranslations[LanguageConfig.english];
  const translations = postTranslations(data.mallTranslations);

  let photo = null;
  console.log('data.coverPhoto?.value', data.coverPhoto?.value);
  console.log('data.coverPhoto', data.coverPhoto);
  if (
    data.coverPhoto?.value === data.photoUrl ||
    data.coverPhoto === data.photoUrl
  ) {
    console.log('data.coverPhoto same as photoUrl');
    photo = null;
  } else {
    photo = getFileNameFromUrl(data.coverPhoto?.value || data.coverPhoto);
  }

  const phoneNumberInfos = cleanInformations(
    enData.emailAddressInfos || {},
    translations,
    'phoneNumberInfos',
  );

  const emailAddressInfos = cleanInformations(
    enData.emailAddressInfos || {},
    translations,
    'emailAddressInfos',
  );

  const additionalInfos = cleanInformations(
    enData.additionalInfos || {},
    translations,
    'additionalInfos',
  );

  let newData = {
    name: name,
    // photo: photo,
    // icon: getFileNameFromUrl(data.icon?.value || data.icon) || null,
    // district: data.district?.value?.pk || null,
    // address: data.mallTranslations[LanguageConfig.english]?.address,
    // phoneNumberInfos,
    // emailAddressInfos,
    // additionalInfos,
    // latitude: data.latitude,
    // longitude: data.longitude,
    thirdPartyMallCode: data.thirdPartyMallCode,
    // visibleInFrontEnd:
    //   data.visibleInFrontEnd === undefined ? true : data.visibleInFrontEnd,
    translations,
  };
  if (data.pk) {
    newData.id = data.pk;
    if (old.brand) {
      delete newData.brand;
    }
  }
  return newData;
};

const parseMall = (mall, minimalist = false) => {
  if (!mall) {
    return {};
  }
  const extraName = mall.visibleInFrontEnd ? '' : '(closed)';
  if (minimalist) {
    return {
      pk: mall.pk,
      mallPK: mall.pk,
      mallName: mall.name,
      name: `${mall.name}${extraName}`,
    };
  }
  return {
    ...mall,
    mallID: mall.id,
    mallPK: mall.pk,
    mallName: mall.name,
    displayMallName: `${mall.name}${extraName}`,
    mallAddress: mall.address,
    mallTranslations: getTranslations(mall),
    photo: mall.photo || mall.photoUrl,
    coverPhoto: mall.photo || mall.photoUrl,
    photoUrl: mall.photoUrl,
    displayDistrict: mall.district?.name,
    district: mall.district
      ? { label: mall.district.name, value: mall.district }
      : null,
    displayName: mall.name,
    displayVisibleInFrontEnd: mall.visibleInFrontEnd ? 'Showing' : 'Hidden',
    listDisplayLastModifiedTime: getListDisplayLastModifiedDate(
      mall?.lastModifiedDate,
    ),
    detailDisplayCreationDate: getDetailPageDisplayLastModifiedDate(
      mall?.creationDate,
    ),
    detailDisplayLastModifiedDate: getDetailPageDisplayLastModifiedDate(
      mall?.lastModifiedDate,
    ),
    displayLastModifiedBy: getDisplayLastModifiedBy(mall),
    phoneNumberInfosKeys: [
      ...Array(mall.phoneNumberInfos?.edges?.length || 1).keys(),
    ],
    emailAddressInfosKeys: [
      ...Array(mall.emailAddressInfos?.edges?.length || 1).keys(),
    ],
    additionalInfosKeys: [
      ...Array(mall.additionalInfos?.edges?.length || 1).keys(),
    ],
  };
};

const parseListMall = (mall) => {
  if (!mall) {
    return {};
  }
  const extraName = mall.visibleInFrontEnd ? '' : '(closed)';
  return {
    pk: mall.pk,
    name: mall.name,
    coverPhoto: mall.photo ? mall.photo : mall.photoUrl,
    displayVisibleInFrontEnd: mall.visibleInFrontEnd ? 'Showing' : 'Hidden',
    listDisplayLastModifiedTime: getListDisplayLastModifiedDate(
      mall?.lastModifiedDate,
    ),

    displayName: mall.name,
    displayDistrict: mall.district?.name,
    mallID: mall.id,
    mallPK: mall.pk,
    mallName: mall.name,
    displayMallName: `${mall.name}${extraName}`,
    mallAddress: mall.address,
    district: mall.district
      ? { label: mall.district.name, value: mall.district }
      : null,
    detailDisplayCreationDate: getDetailPageDisplayLastModifiedDate(
      mall?.creationDate,
    ),
    detailDisplayLastModifiedDate: getDetailPageDisplayLastModifiedDate(
      mall?.lastModifiedDate,
    ),
    displayLastModifiedBy: getDisplayLastModifiedBy(mall),
  };
};

const getInitState = () => {
  return {
    mallList: [],
    allListLoading: false,
    selectedAllListLoading: false,
    mallEditMode: false,
    listDisplayFields: [
      { displayName: 'ID', fieldName: 'pk', orderField: 'pk', linked: true },
      { displayName: 'Name', fieldName: 'name', orderField: 'name' },
      { displayName: 'Cover photo', fieldName: 'coverPhoto' },
      {
        displayName: 'Visible on front end',
        fieldName: 'displayVisibleInFrontEnd',
      },
      {
        displayName: 'Last modified time',
        fieldName: 'listDisplayLastModifiedTime',
      },
    ],
    pagedMallList: [],
    pageInfo: {
      startCursor: '',
      endCursor: '',
      hasNextPage: false,
      hasPreviousPage: false,
    },
    totalPage: 0,
    totalCount: 0,
    currentPage: 0,
    checkedList: [],
    detail: {
      phoneNumberInfosKeys: [...Array(1).keys()],
      emailAddressInfosKeys: [...Array(1).keys()],
      additionalInfosKeys: [...Array(1).keys()],
    },
    checked: CheckStatus.initOrNotChecked,
    errorFields: {},
    saved: APIStatus.none,
    formChanged: false,
    districtList: [],
    formHasSubmitted: false,
    hasUpdatedDefaultValues: false,
    currentLastCursor: '',
  };
};

export default createModel({
  namespace: 'mall',
  state: getInitState(),
  params: {
    sessionKey: mallSessionKey,
    listAPI: getPagedMalls,
    parse: (data) => data?.malls?.edges?.map((item) => item.node),
    objectKey: 'mallList',
    deleteAPI: deleteMalls,
  },
  reducers: {
    updateState(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    loadDataFromCookie(state, { payload }) {
      const sessionMall = getObjectFromSessionStorage(mallSessionKey) || {};
      let tempMall = payload?.data ? payload?.data : {};

      tempMall.name = {
        [LanguageConfig.english]: tempMall.name || null,
        [LanguageConfig.traditionalChinese]: tempMall.mallTranslations
          ? tempMall.mallTranslations[LanguageConfig.traditionalChinese]?.name
          : null,
        [LanguageConfig.simplifiedChinese]: tempMall.mallTranslations
          ? tempMall.mallTranslations[LanguageConfig.simplifiedChinese]?.name
          : null,
      };
      tempMall.address = {
        [LanguageConfig.english]: tempMall.address || null,
        [LanguageConfig.traditionalChinese]: tempMall.mallTranslations
          ? tempMall.mallTranslations[LanguageConfig.traditionalChinese]
              ?.address
          : null,
        [LanguageConfig.simplifiedChinese]: tempMall.mallTranslations
          ? tempMall.mallTranslations[LanguageConfig.simplifiedChinese]?.address
          : null,
      };
      let data = { ...tempMall };
      if (!payload?.isView) {
        data = { ...tempMall, ...removeUndefinedFieldFromDict(sessionMall) };
      }
      return {
        ...state,
        detail: data,
        hasUpdatedDefaultValues: true,
      };
    },
    removeFromCookie(state, { payload }) {
      removeFromSessionStorage(mallSessionKey);
      return { ...state, formChanged: false };
    },
    clearData(state, { payload }) {
      return { ...state, ...getInitState() };
    },
    clearAnotherData(state, { payload }) {
      return { ...state, pagedMallListAnother: [] };
    },
  },
  effects: {
    *getAllMalls({ payload }, { call, put, select }) {
      const response = yield call(getMalls);
      const mallInfo = response.data.data.malls;
      if (mallInfo) {
        const mallList = mallInfo.edges;
        yield put(
          createAction('updateState')({
            mallList: mallList.map((item) => parseMall(item.node)),
          }),
        );
      }
    },
    getPagedMallList: [
      function* ({ payload }, { put, select }) {
        const page = payload.page || 1;
        const pageCursor = payload.page
          ? convertNumberToCursor((page - 1) * 20 - 1)
          : '';
        const serviceArgs = [getPagedMalls, pageCursor, payload];
        function* onSuccess(data) {
          const pageInfo = data?.malls?.pageInfo;
          const currentLastCursor = pageInfo?.endCursor;
          const totalCount = data?.malls?.totalCount;

          const mallList = data?.malls?.edges.map((item) =>
            parseListMall(item.node),
          );
          if (payload.isSelectorLoad) {
            const stateMallList = yield select((state) => state.mall.mallList);
            const newMallList =
              page > 1 ? [...stateMallList, ...mallList] : mallList;
            console.log('???page', page, newMallList);
            yield put({
              type: 'updateState',
              payload: {
                mallList: newMallList,
              },
            });
          } else {
            yield put({
              type: 'updateState',
              payload: {
                pagedMallList: mallList,
                pageInfo: {
                  startCursor: convertCursorToNumber(pageInfo.startCursor) + 1,
                  endCursor: convertCursorToNumber(pageInfo.endCursor) + 1,
                },
                currentLastCursor,
                totalCount,
                totalPage: Math.ceil(totalCount / 20),
              },
            });
          }
        }
        if (payload.noLoading) {
          yield apiWithResponseHandle(serviceArgs, onSuccess);
        } else {
          yield loading(serviceArgs, onSuccess);
        }
      },
      { type: 'takeLatest' },
    ],

    getOneMall: [
      function* ({ payload }, { call, select, put }) {
        const serviceArgs = [getOneMall, convertPKToId('MallNode', payload.id)];
        function* onSuccess(data) {
          console.log('???', data);
          const mall = parseMall(data.mall);
          yield put({
            type: 'updateState',
            payload: {
              detail: mall,
              isView: payload.view || false,
            },
          });
          const action = payload?.afterAction || (() => {});
          yield action(mall);
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    createOrUpdate: [
      function* ({ payload }, { call, select, put }) {
        let data = payload.data;
        // let source = {};
        console.log('@534', payload, data);
        if (!data) {
          return;
        }

        yield put({
          type: 'updateState',
          payload: { saved: APIStatus.calling },
        });
        const oldData = yield select((state) => state.mall.detail);
        const source = postData(data, oldData);
        const afterAction = payload.afterAction || (() => {});
        const serviceArgs = [createOrUpdateMall, source];
        function* onSuccess(data) {
          if (
            ('createMall' in data && data.createMall.errors) ||
            ('updateMall' in data && data.updateMall.errors)
          ) {
            yield put({
              type: 'updateState',
              payload: {
                saved: APIStatus.failed,
                formChanged: false,
                checked: CheckStatus.initOrNotChecked,
              },
            });
          } else {
            const updateData = {
              formChanged: false,
              saved: APIStatus.success,
              checked: CheckStatus.initOrNotChecked,
              formHasSubmitted: true,
            };
            if (data.createMall?.node?.pk) {
              updateData.detail = {
                mallPK: data.createMall?.node?.pk,
              };
            }
            yield put({
              type: 'updateState',
              payload: updateData,
            });

            removeFromSessionStorage(mallSessionKey);
          }

          afterAction();
        }

        function* onFail(data) {
          yield put({
            type: 'updateState',
            payload: {
              formChanged: false,
              saved: APIStatus.failed,
              // formHasSubmitted: true,
              // hasUpdatedDefaultValues: true,
            },
          });
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, onFail, onFail);
      },
      { type: 'takeLatest' },
    ],
    duplicate: [
      function* ({ payload }, { call, select, put }) {
        const duplicateData = payload?.data;
        function* mallGet(mall) {
          console.log('@735 mall', mall);
          const newMall = {
            ...mall,
            mallTranslations: {
              ...mall.mallTranslations,
              en: {
                ...mall.mallTranslations.en,
                name: `Copy of ${mall.name}`,
              },
            },
            pk: null,
            mallPK: null,
          };
          yield put(
            createAction('createOrUpdate')({
              data: newMall,
              afterAction: payload.afterAction,
            }),
          );
        }
        yield put({
          type: 'getOneMall',
          payload: { id: duplicateData?.pk, afterAction: mallGet },
        });
      },
      { type: 'takeLatest' },
    ],
  },
});
